import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Mail, Building2, User, LogIn } from "lucide-react";
import BrochureManager from "@/components/admin/BrochureManager";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Admin = () => {
    const [token, setToken] = useState("");
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [authed, setAuthed] = useState(false);
    const [tab, setTab] = useState("contacts");

    const fetchContacts = async (t) => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/admin/contacts`, { headers: { "X-Admin-Token": t } });
            setContacts(res.data);
            setAuthed(true);
            toast.success(`Loaded ${res.data.length} submissions.`);
        } catch (err) {
            if (err?.response?.status === 401) {
                toast.error("Invalid admin token.");
                setAuthed(false);
            } else {
                toast.error("Unable to load submissions.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="admin-page" className="pt-28 pb-24 min-h-screen">
            <div className="sxe-container">
                <div className="overline"><span className="dot" /> Admin Console</div>
                <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tighter text-white mt-5">Contact Submissions</h1>
                <p className="text-[#B0A3C4] mt-3">Internal view of enterprise inquiries captured on springxe.com.</p>

                <div className="sxe-card p-6 mt-8 max-w-xl">
                    <label className="text-xs uppercase tracking-widest text-[#75668C] font-display">Admin Token</label>
                    <div className="flex gap-3 mt-2">
                        <input
                            data-testid="admin-token-input"
                            type="password"
                            value={token}
                            onChange={(e) => setToken(e.target.value)}
                            placeholder="Enter admin token"
                            className="sxe-input"
                        />
                        <button
                            onClick={() => fetchContacts(token)}
                            disabled={!token || loading}
                            data-testid="admin-load-btn"
                            className="btn-primary shrink-0 disabled:opacity-50"
                        >
                            <LogIn className="w-4 h-4" /> Load
                        </button>
                    </div>
                </div>

                {authed && (
                    <div className="mt-10">
                        <div className="flex items-center gap-2 border-b border-white/10">
                            {[
                                { key: "contacts", label: "Contact Submissions" },
                                { key: "brochures", label: "Partner Brochures" },
                            ].map((t) => (
                                <button
                                    key={t.key}
                                    onClick={() => setTab(t.key)}
                                    data-testid={`admin-tab-${t.key}`}
                                    className={`px-4 py-2.5 text-sm font-display font-medium border-b-2 -mb-px transition-colors ${
                                        tab === t.key
                                            ? "border-[#A100FF] text-white"
                                            : "border-transparent text-[#75668C] hover:text-[#B0A3C4]"
                                    }`}
                                >
                                    {t.label}
                                </button>
                            ))}
                        </div>

                        {tab === "contacts" && (
                            <div className="mt-6">
                                <div className="flex items-center justify-between">
                                    <div className="font-display text-white text-2xl font-medium tracking-tight">
                                        {contacts.length} submission{contacts.length !== 1 ? "s" : ""}
                                    </div>
                                    <button
                                        onClick={() => fetchContacts(token)}
                                        data-testid="admin-refresh-btn"
                                        className="btn-secondary"
                                    >
                                        <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
                                    </button>
                                </div>

                                <div className="grid gap-4 mt-6">
                                    {contacts.map((c) => (
                                        <div key={c.id} className="sxe-card p-6" data-testid={`admin-row-${c.id}`}>
                                            <div className="flex flex-wrap gap-4 items-start justify-between">
                                                <div>
                                                    <div className="flex items-center gap-2 text-white font-display font-medium">
                                                        <User className="w-4 h-4 text-[#C266FF]" /> {c.name}
                                                    </div>
                                                    <div className="flex items-center gap-2 text-[#B0A3C4] text-sm mt-1.5">
                                                        <Mail className="w-3.5 h-3.5" /> {c.email}
                                                    </div>
                                                    {c.company && (
                                                        <div className="flex items-center gap-2 text-[#B0A3C4] text-sm mt-1">
                                                            <Building2 className="w-3.5 h-3.5" /> {c.company}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="text-right">
                                                    <div className="sxe-chip">{c.interest || "General"}</div>
                                                    <div className="text-[11px] text-[#75668C] mt-2 font-mono">
                                                        {new Date(c.created_at).toLocaleString()}
                                                    </div>
                                                </div>
                                            </div>
                                            <p className="text-[#E9CCFF] text-sm leading-relaxed mt-4 whitespace-pre-wrap">{c.message}</p>
                                        </div>
                                    ))}
                                    {contacts.length === 0 && (
                                        <div className="sxe-card p-8 text-center text-[#B0A3C4]">
                                            No submissions yet.
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {tab === "brochures" && <BrochureManager token={token} />}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Admin;
